import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPackage, FiCalendar, FiDollarSign, FiTag, FiCheck, FiX, FiClock, FiLoader } from 'react-icons/fi';

interface Order {
  id: number;
  order_number: string;
  order_type: string;
  status: string;
  subtotal: number;
  discount_amount: number;
  total_amount: number;
  coupon_code: string | null;
  payment_method: string;
  created_at: string;
  items: OrderItem[];
}

interface OrderItem {
  id: number;
  item_type: string;
  item_name: string;
  item_image: string;
  unit_price: number;
  quantity: number;
  total: number;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
}

interface PurchaseHistoryProps {
  userId: number;
}

const PurchaseHistory: React.FC<PurchaseHistoryProps> = ({ userId }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOrders();
  }, [userId]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📦 Fetching orders for user:', userId);
      const { apiUrls } = await import('../config/api');
      const url = apiUrls.payments.userOrders(userId);
      console.log('API URL:', url);
      
      const response = await fetch(url);
      console.log('Response status:', response.status);
      
      const data = await response.json();
      console.log('Orders data:', data);

      if (data.success) {
        setOrders(data.orders || []);
        console.log('Orders loaded:', data.orders?.length || 0);
      } else {
        setError(data.error || 'Failed to load orders');
      }
    } catch (err: any) {
      console.error('Error fetching orders:', err);
      setError(err.message || 'Failed to load purchase history');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: any): string => {
    try {
      const numPrice = Number(price);
      if (isNaN(numPrice)) return '0.00';
      return numPrice.toFixed(2);
    } catch (error) {
      console.error('Error formatting price:', price, error);
      return '0.00';
    }
  };

  const getStatusColor = (status: string) => {
    const colors: {[key: string]: string} = {
      completed: '#10b981',
      pending: '#f59e0b',
      processing: '#3b82f6',
      failed: '#ef4444',
      cancelled: '#6b7280',
      refunded: '#8b5cf6'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusIcon = (status: string) => {
    const icons: {[key: string]: JSX.Element} = {
      completed: <FiCheck />,
      pending: <FiClock />,
      processing: <FiLoader />,
      failed: <FiX />,
      cancelled: <FiX />
    };
    return icons[status] || <FiClock />;
  };

  if (loading) {
    return (
      <LoadingContainer>
        <FiLoader className="spin" /> Loading your purchases...
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <FiX /> {error}
      </ErrorContainer>
    );
  }

  if (orders.length === 0) {
    return (
      <EmptyState>
        <FiPackage size={48} />
        <h3>No Purchases Yet</h3>
        <p>Your purchase history will appear here</p>
      </EmptyState>
    );
  }

  return (
    <Container>
      <Header>
        <Title><FiPackage /> Purchase History</Title>
        <OrderCount>{orders.length} {orders.length === 1 ? 'Order' : 'Orders'}</OrderCount>
      </Header>

      <OrdersList>
        {orders.map((order) => (
          <OrderCard key={order.id}>
            <OrderHeader>
              <OrderInfo>
                <OrderNumber>Order #{order.order_number}</OrderNumber>
                <OrderDate>
                  <FiCalendar size={14} />
                  {new Date(order.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </OrderDate>
              </OrderInfo>
              
              <StatusBadge status={order.status}>
                {getStatusIcon(order.status)}
                {order.status}
              </StatusBadge>
            </OrderHeader>

            <OrderItems>
              {order.items.map((item) => (
                <OrderItemRow key={item.id}>
                  <ItemImage src={item.item_image || '/mathknots-logo.png'} alt={item.item_name} />
                  <ItemInfo>
                    <ItemName>{item.item_name}</ItemName>
                    <ItemMeta>
                      {item.item_type === 'book' ? '📚 Book' : '📦 Package'} • Qty: {item.quantity}
                    </ItemMeta>
                    {item.subscription_end_date && (
                      <SubscriptionInfo>
                        Access until: {new Date(item.subscription_end_date).toLocaleDateString()}
                      </SubscriptionInfo>
                    )}
                  </ItemInfo>
                  <ItemPrice>${formatPrice(item.total)}</ItemPrice>
                </OrderItemRow>
              ))}
            </OrderItems>

            <OrderFooter>
              <PriceBreakdown>
                {order.coupon_code && (
                  <PriceRow>
                    <span>
                      <FiTag size={14} /> Coupon: {order.coupon_code}
                    </span>
                    <span style={{ color: '#10b981' }}>
                      -${formatPrice(order.discount_amount)}
                    </span>
                  </PriceRow>
                )}
                
                <TotalRow>
                  <span>Total Paid:</span>
                  <span>${formatPrice(order.total_amount)}</span>
                </TotalRow>
              </PriceBreakdown>
              
              <PaymentMethod>
                <FiDollarSign size={14} />
                {order.payment_method.toUpperCase()}
              </PaymentMethod>
            </OrderFooter>
          </OrderCard>
        ))}
      </OrdersList>
    </Container>
  );
};

export default PurchaseHistory;

// Styled Components
const Container = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #3b82f6;
  }
`;

const OrderCount = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 600;
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OrderCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const OrderInfo = styled.div``;

const OrderNumber = styled.div`
  font-weight: 700;
  color: #1f2937;
  font-size: 16px;
  margin-bottom: 4px;
`;

const OrderDate = styled.div`
  font-size: 13px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  text-transform: capitalize;
  background: ${props => {
    const colors: {[key: string]: string} = {
      completed: '#d1fae5',
      pending: '#fef3c7',
      processing: '#dbeafe',
      failed: '#fee2e2',
      cancelled: '#f3f4f6'
    };
    return colors[props.status] || '#f3f4f6';
  }};
  color: ${props => {
    const colors: {[key: string]: string} = {
      completed: '#065f46',
      pending: '#92400e',
      processing: '#1e40af',
      failed: '#991b1b',
      cancelled: '#374151'
    };
    return colors[props.status] || '#374151';
  }};
`;

const OrderItems = styled.div`
  padding: 20px;
`;

const OrderItemRow = styled.div`
  display: flex;
  gap: 16px;
  padding: 12px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
    margin-bottom: 12px;
  }
`;

const ItemImage = styled.img`
  width: 50px;
  height: 66px;
  object-fit: cover;
  border-radius: 4px;
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemName = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
`;

const ItemMeta = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const SubscriptionInfo = styled.div`
  font-size: 12px;
  color: #10b981;
  font-weight: 500;
`;

const ItemPrice = styled.div`
  font-weight: 700;
  color: #1f2937;
`;

const OrderFooter = styled.div`
  padding: 16px 20px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PriceBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6b7280;

  span:last-child {
    font-weight: 600;
  }
`;

const TotalRow = styled.div`
  display: flex;
  gap: 12px;
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
`;

const PaymentMethod = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px;
  color: #6b7280;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px;
  color: #ef4444;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px;
  color: #6b7280;

  svg {
    margin-bottom: 16px;
    opacity: 0.5;
  }

  h3 {
    font-size: 20px;
    color: #1f2937;
    margin: 0 0 8px 0;
  }

  p {
    margin: 0;
  }
`;

